// External libraries
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Globe, Volume2, VolumeX, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { tamilUtils } from '@/lib/i18n-config';

const languages = [
  {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    flag: '🇺🇸',
    direction: 'ltr',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  {
    code: 'ta',
    name: 'Tamil',
    nativeName: 'தமிழ்',
    flag: '🇮🇳',
    direction: 'ltr',
    fontFamily: "'Noto Sans Tamil', 'Lohit Tamil', 'Tamil MN', sans-serif",
  },
];

export default function LanguageSelector() {
  const { i18n, t } = useTranslation();
  const [isTTS, setIsTTS] = useState(() => localStorage.getItem('voterVerify_tts') === 'true');
  const [fontSize, setFontSize] = useState(() =>
    localStorage.getItem('voterVerify_font_size') || 'normal'
  );

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];
  const isTamil = tamilUtils.isTamilLanguage();

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    localStorage.setItem('voterVerify_language', languageCode);
  };

  const toggleTTS = () => {
    const newTTS = !isTTS;
    setIsTTS(newTTS);
    localStorage.setItem('voterVerify_tts', newTTS.toString());
    
    // Trigger TTS preference change event
    window.dispatchEvent(new CustomEvent('ttsPreferenceChange', { 
      detail: { enabled: newTTS } 
    }));
  };

  const changeFontSize = (size: string) => {
    setFontSize(size);
    localStorage.setItem('voterVerify_font_size', size);
    
    const root = document.documentElement;
    const fontSizeMap = {
      small: '14px',
      normal: '16px',
      large: '18px',
      'extra-large': '20px'
    };
    root.style.fontSize = fontSizeMap[size as keyof typeof fontSizeMap] || '16px';
  };

  const getFontSizeIcon = (size: string) => {
    return cn(
      "w-2 h-2 rounded-full bg-current",
      fontSize === size && "ring-2 ring-offset-2 ring-current"
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "flex items-center gap-2 px-3",
            isTamil && tamilUtils.getTextClass('ui')
          )}
          aria-label="Language selector"
        >
          <Globe className="h-4 w-4" />
          <span className="hidden sm:inline font-medium">
            {currentLanguage.flag} {currentLanguage.nativeName}
          </span>
          <span className="sm:hidden">
            {currentLanguage.flag}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-64 backdrop-blur-lg bg-white/95 dark:bg-gray-900/95 border border-white/20 dark:border-gray-700/20 shadow-xl"
      >
        <div className="px-2 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
          {isTamil ? 'மொழி தேர்வு' : 'Language Selection'}
        </div>
        
        {languages.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            className={cn(
              "flex items-center gap-3 py-2 px-2 cursor-pointer transition-colors",
              i18n.language === language.code && "bg-purple-50 dark:bg-purple-900/20"
            )}
          >
            <span className="text-2xl">{language.flag}</span>
            <div className="flex-1">
              <div className={cn(
                "font-medium",
                language.code === 'ta' && "tamil-heading"
              )}>
                {language.nativeName}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {language.name}
              </div>
            </div>
            {i18n.language === language.code && (
              <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
            )}
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />

        <div className="px-2 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
          {isTamil ? 'அமைப்புகள்' : 'Settings'}
        </div>

        <DropdownMenuItem
          onClick={toggleTTS}
          className="flex items-center gap-3 py-2 px-2 cursor-pointer"
        >
          {isTTS ? (
            <Volume2 className="w-4 h-4 text-green-600 dark:text-green-400" />
          ) : (
            <VolumeX className="w-4 h-4 text-gray-400" />
          )}
          <div className="flex-1">
            <div className={cn("font-medium", isTamil && "tamil-ui")}>
              {isTTS ? (isTamil ? 'ஒலி ஆன்' : 'TTS On') : (isTamil ? 'ஒலி ஆஃப்' : 'TTS Off')}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {isTamil ? 'உரை அறிவிப்பு' : 'Text-to-Speech'}
            </div>
          </div>
        </DropdownMenuItem>

        <div className="px-2 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
          {isTamil ? 'எழுத்துரு அளவு' : 'Font Size'}
        </div>

        <div className="px-2 pb-2">
          <div className="grid grid-cols-2 gap-2">
            {[
              { size: 'small', label: isTamil ? 'சிறிய' : 'Small' },
              { size: 'normal', label: isTamil ? 'இயல்பு' : 'Normal' },
              { size: 'large', label: isTamil ? 'பெரிய' : 'Large' },
              { size: 'extra-large', label: isTamil ? 'மிகப்பெரிய' : 'X-Large' }
            ].map(({ size, label }) => (
              <button
                key={size}
                onClick={() => changeFontSize(size)}
                className={cn(
                  "flex items-center gap-2 px-2 py-1.5 text-sm rounded-md transition-colors",
                  fontSize === size 
                    ? "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300" 
                    : "hover:bg-gray-100 dark:hover:bg-gray-800"
                )}
              >
                <div className={getFontSizeIcon(size)} />
                <span className={cn(isTamil && "tamil-ui")}>{label}</span>
              </button>
            ))}
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}